import { z } from 'zod';

/**
 * Contracts for the admin coupon-package endpoints (admin/coupons…).
 *
 * @typedef {'draft'|'active'|'archived'} PackageStatus
 * @typedef {{ userId: string, name: string|null }} Actor
 * @typedef {{ level: number, scansRequired: number|null, tierName: string|null, couponId: number|null,
 *   brandName: string, logoUrl: string|null, offerText: string, offerSubtitle: string|null,
 *   couponCode: string|null, validForDays: number|null }} Level
 * @typedef {{ id: number, name: string|null, status: PackageStatus, createdAt: string|null, updatedAt: string|null,
 *   createdBy: Actor|null, updatedBy: Actor|null, levels: Level[] }} Package
 * @typedef {{ level: number, scansRequired: number, name: string }} Tier
 * @typedef {{ all: number, active: number, draft: number, archived: number }} StatusCounts
 * @typedef {{ page: number, limit: number, totalItems: number, totalPages: number, hasNextPage: boolean, hasPrevPage: boolean }} Pagination
 * @typedef {{ coupons: Package[], pagination: Pagination, tiers: Tier[], brands: string[], statusCounts: StatusCounts }} PackageList
 * @typedef {{ brandName: string, logoUrl: string|null, offerText: string, offerSubtitle: string|null,
 *   couponCode: string|null, validForDays: number|null }} LevelInput  one item of the POST/PUT body
 * @typedef {{ name: string|null, status: PackageStatus, levels: LevelInput[] }} PackageInput
 */

export const STATUSES = ['draft', 'active', 'archived'];

const nullableStr = z.string().nullable().optional().transform((v) => v ?? null);
const actor = z
  .object({ userId: z.string(), name: z.string().nullable().optional() })
  .nullable()
  .optional()
  .transform((v) => (v ? { userId: v.userId, name: v.name ?? null } : null));

export const levelSchema = z
  .object({
    level: z.number().int().min(1),
    scansRequired: z.number().int().nonnegative().nullable().optional().transform((v) => v ?? null),
    tierName: nullableStr,
    couponId: z.number().int().nullable().optional().transform((v) => v ?? null),
    brandName: z.string(),
    logoUrl: nullableStr,
    offerText: z.string().optional(),
    // Older responses only had couponDetails; offerText is the field to use
    couponDetails: z.string().optional(),
    offerSubtitle: nullableStr,
    couponCode: nullableStr,
    validForDays: z.number().int().positive().nullable().optional().transform((v) => v ?? null),
  })
  .transform(({ couponDetails, offerText, ...level }) => ({ ...level, offerText: offerText ?? couponDetails ?? '' }));

export const packageSchema = z.object({
  id: z.number().int().positive(),
  name: nullableStr,
  status: z.enum(STATUSES),
  createdAt: nullableStr,
  updatedAt: nullableStr,
  createdBy: actor,
  updatedBy: actor,
  levels: z.array(levelSchema),
});

export const tierSchema = z.object({
  level: z.number().int().min(1),
  scansRequired: z.number().int().nonnegative(),
  name: z.string(),
});

export const packageListSchema = z.object({
  coupons: z.array(z.unknown()),
  pagination: z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
  }),
  tiers: z.array(tierSchema),
  brands: z.array(z.string()),
  statusCounts: z
    .object({ all: z.number(), active: z.number(), draft: z.number(), archived: z.number() })
    .partial()
    .transform((c) => ({ all: c.all ?? 0, active: c.active ?? 0, draft: c.draft ?? 0, archived: c.archived ?? 0 })),
});
